import { Fragment, useMemo, useState } from "react";
import {
  Alert, Box, Breadcrumbs, Button, CircularProgress, Link, Paper, Stack, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Tabs, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { errorMessage, fetchPeriod } from "@/api/closeReview";
import type { CheckSummary, Report, ReportKey } from "@/api/closeReview";
import { formatValue, needsReview, NotYet, ReportTable, ReviewState, STATUS_ORDER, StatusChip } from "./shared";
import { StatJournals } from "./StatJournals";

type TabKey = "checks" | ReportKey;
const TABS: { key: TabKey; label: string }[] = [
  { key: "checks", label: "Checks" },
  { key: "pl", label: "P&L" },
  { key: "bs", label: "Balance Sheet" },
  { key: "stat_journals", label: "Statistical Journals" },
];
type Filter = "all" | "attention" | "flagged" | "info" | "passing";

const isFlagged = (c: CheckSummary) => c.status === "flag" || c.status === "error";

function matches(c: CheckSummary, filter: Filter): boolean {
  if (filter === "attention") return needsReview(c);
  if (filter === "flagged") return isFlagged(c);
  if (filter === "info") return c.status === "info";
  if (filter === "passing") return c.status === "pass";
  return true;
}

const threshold = (t: number) => (t < 100 ? `$${t.toFixed(2)}` : formatValue(t, "money"));

function Summary({ checks, filter, onFilter }: { checks: CheckSummary[]; filter: Filter; onFilter: (f: Filter) => void }) {
  const attention = checks.filter(needsReview).length;
  const flags = checks.filter(isFlagged).length;
  const passing = checks.filter((c) => c.status === "pass").length;
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
      <Stack direction="row" spacing={3}>
        {[
          { n: attention, label: "need review", color: attention ? "warning.main" : "text.secondary" },
          { n: flags, label: "flagged", color: "text.primary" },
          { n: passing, label: "passing", color: "success.main" },
          { n: checks.length, label: "checks", color: "text.secondary" },
        ].map((s) => (
          <Box key={s.label}>
            <Typography variant="h5" sx={{ color: s.color, lineHeight: 1.1 }}>{s.n}</Typography>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Box>
        ))}
      </Stack>
      <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v: Filter | null) => v && onFilter(v)} sx={{ flexWrap: "wrap" }}>
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="attention">Needs review</ToggleButton>
        <ToggleButton value="flagged">Flagged</ToggleButton>
        <ToggleButton value="info">Info</ToggleButton>
        <ToggleButton value="passing">Passing</ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
}

function ChecksTable({ month, checks, filter }: { month: string; checks: CheckSummary[]; filter: Filter }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const groups = useMemo(() => {
    const shown = checks.filter((c) => matches(c, filter));
    const out = new Map<string, CheckSummary[]>();
    shown.forEach((c) => out.set(c.group, [...(out.get(c.group) ?? []), c]));
    return [...out.entries()].map(([group, rows]) =>
      [group, [...rows].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status])] as const);
  }, [checks, filter]);
  const open = (c: CheckSummary) => navigate(`/close-review/${month}/${encodeURIComponent(c.key)}`);
  const toggle = (g: string) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(g)) n.delete(g); else n.add(g);
    return n;
  });

  if (groups.length === 0) return <Alert severity="success" variant="outlined">Nothing here: every check in this view is clear.</Alert>;
  return (
    <Paper variant="outlined" sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
        <colgroup>
          <col />
          <col style={{ width: 88 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 190 }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell>Check</TableCell>
            <TableCell>Result</TableCell>
            <TableCell align="right">Difference</TableCell>
            <TableCell>Review</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map(([group, rows]) => {
            // A group with nothing to look at folds into one line.
            const allClear = filter === "all" && rows.every((c) => c.status === "pass" && !needsReview(c));
            const folded = allClear && !expanded.has(group);
            return (
              <Fragment key={group}>
                <TableRow onClick={allClear ? () => toggle(group) : undefined} sx={{ cursor: allClear ? "pointer" : "default" }}>
                  <TableCell colSpan={4} sx={{ bgcolor: "action.hover", py: 0.75 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {allClear && (folded ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
                      <Typography variant="subtitle2">{group}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {allClear ? `${rows.length} checks, all passing` : `${rows.length} check${rows.length === 1 ? "" : "s"}`}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
                {!folded && rows.map((c) => (
                  <TableRow key={c.key} hover tabIndex={0} role="link" onClick={() => open(c)}
                    onKeyDown={(e) => { if (e.key === "Enter") open(c); }} sx={{ cursor: "pointer" }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.title}</Typography>
                        {c.comment_count > 0 && (
                          <Stack direction="row" spacing={0.25} alignItems="center"
                            sx={{ color: c.open_comment_count ? "primary.main" : "text.disabled" }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                            <Typography variant="caption">{c.comment_count}</Typography>
                          </Stack>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary"
                        sx={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {c.summary}
                      </Typography>
                    </TableCell>
                    <TableCell><StatusChip status={c.status} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {c.difference !== null
                        ? formatValue(c.difference, "money")
                        : <Typography component="span" color="text.disabled">–</Typography>}
                      {c.threshold !== null && c.difference !== null && (
                        <Typography variant="caption" color="text.secondary" display="block">within {threshold(c.threshold)}</Typography>
                      )}
                    </TableCell>
                    <TableCell><ReviewState check={c} /></TableCell>
                  </TableRow>
                ))}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}

function ReportPanel({ report, note }: { report: Report | undefined; note: string }) {
  if (!report) return <NotYet note={note} />;
  if (report.status === "error") return <Alert severity="error">{report.note}</Alert>;
  return (
    <Stack spacing={1}>
      <Alert severity={report.status === "final" ? "success" : "warning"} variant="outlined">
        {report.note} Updated {dayjs(report.run_at).format("MMM D, h:mm A")}.
      </Alert>
      <Paper variant="outlined" sx={{ overflow: "auto", maxHeight: "calc(100vh - 260px)" }}>
        <ReportTable columns={report.columns} rows={report.rows} />
      </Paper>
    </Stack>
  );
}

export function CloseReviewPeriodPage() {
  const { month = "" } = useParams();
  // ?tab=stat_journals (from an approval email) opens that tab.
  const [search] = useSearchParams();
  const initialTab = TABS.some((t) => t.key === search.get("tab")) ? (search.get("tab") as TabKey) : "checks";
  const [tab, setTab] = useState<TabKey>(initialTab);
  const [filter, setFilter] = useState<Filter>("all");
  const page = useQuery({ queryKey: ["close-review", "period", month], queryFn: () => fetchPeriod(month), retry: false });
  const period = page.data?.period;
  const reports = new Map((page.data?.reports ?? []).map((r) => [r.key, r]));
  const checks = page.data?.checks ?? [];

  return (
    <AppLayout maxWidth="xl">
      <Stack spacing={2}>
        <Breadcrumbs>
          <Link component={RouterLink} to="/close-review" underline="hover">Close Review</Link>
          <Typography color="text.primary">{period?.label ?? month}</Typography>
        </Breadcrumbs>
        {page.isLoading && <CircularProgress />}
        {page.isError && <Alert severity="error">{errorMessage(page.error)}</Alert>}
        {period && (
          <>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-start" }} spacing={1}>
              <Box>
                <Typography variant="h5">{period.label} close</Typography>
                <Typography variant="body2" color="text.secondary">
                  {period.latest_run_at
                    ? `Last run ${dayjs(period.latest_run_at).format("ddd MMM D, h:mm A")}${period.business_day ? `, business day ${period.business_day}` : ""}. `
                    : ""}
                  {period.schedule.notes.checks}
                </Typography>
              </Box>
              {period.links.package && (
                <Button variant="outlined" size="small" href={period.links.package} target="_blank" rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon fontSize="small" />} sx={{ flexShrink: 0 }}>
                  Financial package
                </Button>
              )}
            </Stack>
            <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
              {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
            </Tabs>
            {tab === "checks" && (checks.length ? (
              <Stack spacing={2}>
                <Summary checks={checks} filter={filter} onFilter={setFilter} />
                <ChecksTable month={month} checks={checks} filter={filter} />
              </Stack>
            ) : <NotYet note={period.schedule.notes.checks} />)}
            {tab === "stat_journals" && (page.data?.stat_journals.length ?? 0) > 0 ? (
              <StatJournals journals={page.data!.stat_journals} month={month} canApprove={page.data!.can_approve_stat_journals} />
            ) : tab !== "checks" && <ReportPanel report={reports.get(tab)} note={period.schedule.notes[tab]} />}
          </>
        )}
      </Stack>
    </AppLayout>
  );
}
